import type { DSNumberFieldModelValue } from "@/src/design_system/form_elements/number_field/types";

interface DSNumberFieldValueOptions {
  allowNegative?: boolean;
  max?: number;
  min?: number;
  precision?: number;
}

export function parseNumberFieldValue(value: string, options: DSNumberFieldValueOptions): number | null {
  const normalizedValue = normalizeInputValue(value);
  if (!normalizedValue) {
    return null;
  }

  if (!isNegativeValueAllowed(normalizedValue, options.allowNegative)) {
    return null;
  }

  const parsedValue = parseNumericCandidate(normalizedValue, options.allowNegative);
  if (parsedValue === null) {
    return null;
  }

  return normalizeNumberFieldValue(parsedValue, options);
}

export function normalizeNumberFieldValue(value: number, options: DSNumberFieldValueOptions): number {
  const boundedValue = clampNumberFieldValue(value, options);
  return applyNumberFieldPrecision(boundedValue, options.precision);
}

export function formatNumberFieldValue(value: DSNumberFieldModelValue, options: DSNumberFieldValueOptions): string {
  const numericValue = toNumberFieldValue(value);
  if (numericValue === null) {
    return "";
  }

  return formatLocalizedNumber(numericValue, options.precision);
}

function isNegativeValueAllowed(normalizedValue: string, allowNegative: boolean | undefined): boolean {
  return allowNegative || !normalizedValue.includes("-");
}

function normalizeInputValue(value: string): string {
  return value.trim().replace(/\s/g, "");
}

function parseNumericCandidate(normalizedValue: string, allowNegative: boolean | undefined): number | null {
  const candidate = buildNumericCandidate(normalizedValue, allowNegative);
  if (!candidate || candidate === "-") {
    return null;
  }

  const parsedValue = Number(candidate);
  return Number.isNaN(parsedValue) ? null : parsedValue;
}

function buildNumericCandidate(normalizedValue: string, allowNegative: boolean | undefined): string {
  const isNegative = Boolean(allowNegative && normalizedValue.startsWith("-"));
  const unsignedValue = normalizedValue.replace(/-/g, "");
  const [integerDigits, decimalDigits] = extractNumberParts(unsignedValue);

  return `${isNegative ? "-" : ""}${integerDigits}${decimalDigits ? `.${decimalDigits}` : ""}`;
}

function extractNumberParts(unsignedValue: string): [string, string] {
  const decimalSeparator = detectDecimalSeparator(unsignedValue);
  if (!decimalSeparator) {
    return [unsignedValue.replace(/\D/g, ""), ""];
  }

  const separatorIndex = unsignedValue.lastIndexOf(decimalSeparator);
  if (separatorIndex === -1) {
    return [unsignedValue.replace(/\D/g, ""), ""];
  }

  const integerPart = unsignedValue.slice(0, separatorIndex).replace(/\D/g, "");
  const decimalPart = unsignedValue.slice(separatorIndex + 1).replace(/\D/g, "");
  return [integerPart, decimalPart];
}

function detectDecimalSeparator(unsignedValue: string): "." | "," | null {
  const separators = getPresentSeparators(unsignedValue);
  if (separators.length === 2) {
    return getLastSeparator(unsignedValue);
  }

  if (separators.length !== 1) {
    return null;
  }

  const [separator] = separators;
  return isThousandsSeparatedInteger(unsignedValue, separator) ? null : separator;
}

function getPresentSeparators(unsignedValue: string): Array<"." | ","> {
  return [".", ","].filter((separator): separator is "." | "," => unsignedValue.includes(separator));
}

function getLastSeparator(unsignedValue: string): "." | "," {
  return unsignedValue.lastIndexOf(".") > unsignedValue.lastIndexOf(",") ? "." : ",";
}

function isThousandsSeparatedInteger(unsignedValue: string, separator: "." | ","): boolean {
  if (!unsignedValue.includes(separator)) {
    return false;
  }

  const parts = unsignedValue.split(separator);
  if (parts.length < 2) {
    return false;
  }

  return parts.slice(1).every(part => part.replace(/\D/g, "").length === 3);
}

function clampNumberFieldValue(value: number, options: DSNumberFieldValueOptions): number {
  if (options.min !== undefined && value < options.min) {
    return options.min;
  }

  if (options.max !== undefined && value > options.max) {
    return options.max;
  }

  return value;
}

function applyNumberFieldPrecision(value: number, precision: number | undefined): number {
  return precision === undefined ? value : Number(value.toFixed(precision));
}

function toNumberFieldValue(value: DSNumberFieldModelValue): number | null {
  if (value === null || value === undefined) {
    return null;
  }

  const numericValue = Number(value);
  return Number.isNaN(numericValue) ? null : numericValue;
}

function formatLocalizedNumber(value: number, precision: number | undefined): string {
  return new Intl.NumberFormat(getCurrentUserLanguage(), getNumberFormatOptions(precision)).format(value);
}

function getCurrentUserLanguage(): string | undefined {
  return window.EASY?.currentUser?.language;
}

function getNumberFormatOptions(precision: number | undefined): Intl.NumberFormatOptions {
  if (precision === undefined) {
    return {
      useGrouping: false,
      maximumFractionDigits: 20,
    };
  }

  return {
    useGrouping: false,
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  };
}
